<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"

import { useSearch, type SearchEntry } from "@/composables/use-search"
import { useSearchIndex } from "@/composables/use-search-index"

const route = useRoute()
const router = useRouter()

// Let the host (the menu nav list) collapse once a suggestion is chosen — the
// nav-open state has no active content view, so the machine's route-change
// watcher won't close it for us.
const emit = defineEmits<{ close: [] }>()

// Shared corpus (students + programmes). Loads lazily once the projects store
// resolves; until then Fuse simply searches an empty collection.
const { entries, ensureIndex } = useSearchIndex()
const { searchQuery, fuseResults, getMostRelevantField } = useSearch(entries)

// ─── Autocomplete dropdown ──────────────────────────────────────────────────
// Top matches across all fields. `activeIndex` tracks keyboard selection
// (-1 = nothing highlighted → Enter submits the full results page instead).
const MAX_SUGGESTIONS = 6
const suggestions = computed(() => fuseResults.value.slice(0, MAX_SUGGESTIONS))
const activeIndex = ref(-1)
const showSuggestions = ref(false)

const labelFor = (result: (typeof suggestions.value)[number]) =>
  getMostRelevantField(result).value
const kindFor = (item: SearchEntry) =>
  item.programme ? "Programme" : "Project"

// Reset the highlight whenever the result set changes so we never point at a
// stale row.
watch(suggestions, () => {
  activeIndex.value = -1
})

watch(searchQuery, query => {
  showSuggestions.value = !!query
})

// ─── Navigation ──────────────────────────────────────────────────────────--
const closeSuggestions = () => {
  showSuggestions.value = false
  activeIndex.value = -1
}

const goToSuggestion = (item: SearchEntry) => {
  closeSuggestions()
  emit("close")
  router.push(`/${item.uri}`)
}

// Drive the menu machine's `?view=search` content panel and carry the query in
// `?q=` so results are shareable. Preserve every other param (notably
// `api`/`preview`, which the router guard depends on).
const submitFullSearch = () => {
  if (!searchQuery.value) return
  closeSuggestions()
  router.push({
    query: { ...route.query, view: "search", q: searchQuery.value },
  })
}

const onKeydown = (e: KeyboardEvent) => {
  switch (e.key) {
    case "ArrowDown":
      if (!suggestions.value.length) return
      e.preventDefault()
      activeIndex.value = (activeIndex.value + 1) % suggestions.value.length
      break
    case "ArrowUp":
      if (!suggestions.value.length) return
      e.preventDefault()
      activeIndex.value =
        activeIndex.value <= 0
          ? suggestions.value.length - 1
          : activeIndex.value - 1
      break
    case "Enter":
      // A highlighted suggestion jumps straight there; otherwise fall back to
      // the full results page (the original Enter-to-submit behaviour).
      if (activeIndex.value >= 0 && suggestions.value[activeIndex.value]) {
        goToSuggestion(suggestions.value[activeIndex.value].item)
      } else {
        submitFullSearch()
      }
      break
    case "Escape":
      closeSuggestions()
      break
  }
}

// Delay closing on blur so a mousedown on a suggestion still registers.
const onBlur = () => {
  setTimeout(closeSuggestions, 120)
}

onMounted(() => {
  searchQuery.value = route.query.q as string
  ensureIndex()
})
</script>

<template>
  <div class="relative">
    <input
      class="inline-block w-full bg-transparent px-2 placeholder:text-current focus:outline-hidden placeholder:opacity-50 transition-opacity"
      v-model="searchQuery"
      :placeholder="'Search...'"
      type="text"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="showSuggestions && suggestions.length > 0"
      autocomplete="off"
      @keydown="onKeydown"
      @focus="showSuggestions = !!searchQuery"
      @blur="onBlur"
    />

    <ul
      v-if="showSuggestions && suggestions.length"
      class="absolute left-0 right-0 top-full z-40 mt-1 bg-white border rounded overflow-hidden"
      role="listbox"
    >
      <li
        v-for="(result, index) in suggestions"
        :key="result.item.id"
        role="option"
        :aria-selected="index === activeIndex"
        class="flex items-baseline justify-between gap-2 px-2 py-1 text-sm cursor-pointer transition-colors"
        :class="
          index === activeIndex
            ? 'bg-blue text-white'
            : 'md:hover:bg-blue md:hover:text-white'
        "
        @mousedown.prevent="goToSuggestion(result.item)"
        @mouseenter="activeIndex = index"
      >
        <span class="truncate" v-html="labelFor(result)" />
        <span class="shrink-0 opacity-50 text-xs">{{
          kindFor(result.item)
        }}</span>
      </li>
    </ul>
  </div>
</template>
